import React, { useState, useEffect, useMemo } from 'react';
import { 
  LuTrash2, LuMail, LuTag, LuType, LuAlignLeft, LuCalendar, 
  LuCircleCheck, LuClock, LuCircle, LuSearch, LuFilter, 
  LuDownload, LuX, LuHistory, LuChevronRight, LuUsers, LuMessageSquare
} from 'react-icons/lu';
import { CSVLink } from 'react-csv';
import axios from 'axios';
import moment from 'moment';
import SkeletonLoader from '../../components/ui/SkeletonLoader';

const QueryManagement = ({ onCountChange }) => {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  
  const [showFilters, setShowFilters] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
  });

  useEffect(() => {
    fetchQueries();
  }, []);

  const fetchQueries = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/contact/admin/queries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setQueries(response.data.data);
        if (typeof onCountChange === 'function') {
          onCountChange(response.data.data.length);
        }
      }
    } catch (error) {
      setError('Failed to fetch queries');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    setUpdatingStatus(id);
    const token = localStorage.getItem('adminToken');
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/contact/admin/queries/${id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchQueries();
    } catch (error) {
      setError('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this query?')) return;
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/contact/admin/queries/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchQueries();
    } catch (error) {
      setError('Failed to delete query');
    }
  };

  const filteredQueries = useMemo(() => {
    return queries.filter(query => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || (
        query.email?.toLowerCase().includes(q) ||
        query.category?.toLowerCase().includes(q) ||
        query.subject?.toLowerCase().includes(q)
      );
      const matchesStatus = filters.status === 'all' || query.status === filters.status;
      return matchesSearch && matchesStatus;
    });
  }, [queries, searchQuery, filters]);

  const csvData = useMemo(() => {
    return filteredQueries.map(r => ({
      Email: r.email,
      Category: r.category,
      Subject: r.subject,
      Status: r.status,
      SubmittedAt: moment(r.createdAt).format('YYYY-MM-DD HH:mm:ss')
    }));
  }, [filteredQueries]);

  if (loading) {
    return <SkeletonLoader variant="table" rows={8} cols={5} />;
  }

  return (
    <div className="p-6 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'All Queries', value: queries.length, icon: LuMessageSquare, iconBg: 'bg-primary-100 dark:bg-primary-900/30', iconColor: 'text-primary-600 dark:text-primary-400' },
            { label: 'Open', value: queries.filter(r => r.status === 'open').length, icon: LuCircle, iconBg: 'bg-yellow-100 dark:bg-yellow-900/30', iconColor: 'text-yellow-600 dark:text-yellow-400' },
            { label: 'In Progress', value: queries.filter(r => r.status === 'in-progress').length, icon: LuClock, iconBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-600 dark:text-blue-400' },
            { label: 'Resolved', value: queries.filter(r => r.status === 'resolved').length, icon: LuCircleCheck, iconBg: 'bg-green-100 dark:bg-green-900/30', iconColor: 'text-green-600 dark:text-green-400' },
          ].map((stat, i) => (
            <div key={i} className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-5 shadow-card">
              <div className="flex items-center gap-3">
                <div className={`p-2.5 rounded-xl ${stat.iconBg}`}><stat.icon className={`w-5 h-5 ${stat.iconColor}`} /></div>
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-grow w-full">
              <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-500 w-4 h-4" />
              <input
                type="text"
                placeholder="Search email, subject, category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all font-medium text-sm"
              />
            </div>
            <div className="flex items-center gap-3 w-full md:w-auto">
              <button
                onClick={() => setSelectionMode(!selectionMode)}
                className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all whitespace-nowrap ${selectionMode ? "bg-primary-600 text-white shadow-md shadow-primary-500/30" : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200"}`}
              >
                <LuCircleCheck />
                {selectionMode ? "Active" : "Select"}
              </button>
              <CSVLink
                data={csvData}
                filename={`Queries_${moment().format('YYYY-MM-DD')}.csv`}
                className="flex items-center justify-center gap-3 px-5 py-2.5 bg-green-600 text-white rounded-xl font-bold text-xs hover:bg-green-700 transition-all shadow-md shadow-green-500/30 whitespace-nowrap"
              >
                <LuDownload size={18} />
                Export CSV
              </CSVLink>
            </div>
          </div>
        </div>

      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-max">
            <thead>
              <tr className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
                <th className="px-4 py-4 text-left text-sm font-semibold">Email</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Category</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Subject</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Status</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Submitted</th>
                <th className="px-4 py-4 text-right text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-dark-800">
              {filteredQueries.map((query) => (
                <tr key={query._id} className="hover:bg-primary-50/20 dark:hover:bg-primary-900/10 cursor-pointer" onClick={() => setSelectedQuery(query)}>
                  <td className="px-6 py-4 text-xs font-bold text-primary-600">{query.email}</td>
                  <td className="px-6 py-4"><span className="px-2 py-1 rounded-lg bg-gray-100 text-[10px] font-black uppercase">{query.category}</span></td>
                  <td className="px-6 py-4 text-xs font-bold text-gray-900 line-clamp-1">{query.subject}</td>
                  <td className="px-6 py-4">
                    <select
                      value={query.status}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => handleStatusUpdate(query._id, e.target.value)}
                      className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase border transition-colors ${
                        query.status === 'open' ? 'bg-yellow-50 text-yellow-700 border-yellow-100' :
                        query.status === 'in-progress' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                        'bg-green-50 text-green-700 border-green-100'
                      }`}
                    >
                      <option value="open">Open</option>
                      <option value="in-progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 text-xs font-bold text-gray-500">{moment(query.createdAt).format('MMM DD, YYYY')}</td>
                  <td className="px-6 py-4 text-right sticky right-0 bg-white dark:bg-dark-800 shadow-left group-hover:bg-primary-50/20"><button className="px-3 py-1.5 text-[10px] font-black uppercase bg-gray-100 rounded-lg">View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedQuery && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setSelectedQuery(null)}>
          <div className="bg-white dark:bg-dark-800 rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-xl font-bold">Query Details</h2>
              <button onClick={() => setSelectedQuery(null)} className="p-2 hover:bg-gray-100 rounded-xl"><LuX /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-4 bg-gray-50 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">Subject</label><p className="font-bold">{selectedQuery.subject}</p></div>
              <div className="p-4 bg-gray-50 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">Description</label><p className="font-medium italic leading-relaxed text-sm">"{selectedQuery.description}"</p></div>
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button onClick={() => setSelectedQuery(null)} className="px-6 py-2 bg-gray-100 rounded-xl font-bold text-xs uppercase">Close</button>
              <button onClick={() => { handleDelete(selectedQuery._id); setSelectedQuery(null); }} className="px-6 py-2 bg-red-600 text-white rounded-xl font-bold text-xs uppercase">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueryManagement;
